/**
 * Map Product Variants Page
 * Admin page to map products as variants of a primary product
 * Variants appear as thumbnail strips in the customer mobile app
 */

import React, { useState, useEffect, useCallback } from "react";
import { FaPlus, FaTrash, FaSearch, FaChevronDown, FaChevronUp, FaTimes, FaCheck, FaBoxes } from "react-icons/fa";
import { FiEdit2 } from "react-icons/fi";
import { BiLinkAlt } from "react-icons/bi";
import { 
    getProductVariantMappings, 
    getProductsForVariantMapping, 
    createProductVariantMapping, 
    updateProductVariantMapping, 
    deleteProductVariantMapping 
} from "../../services/ClientService";
import { toast } from "react-toastify";

// Debounce hook for search
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        
        return () => clearTimeout(handler);
    }, [value, delay]);
    
    return debouncedValue;
};

const MapProductVariants = () => {
    // State for mappings list
    const [mappings, setMappings] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearch = useDebounce(searchTerm, 500);

    // State for modal
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingMapping, setEditingMapping] = useState(null);
    const [selectedPrimary, setSelectedPrimary] = useState(null);
    const [selectedVariants, setSelectedVariants] = useState([]);
    const [availableProducts, setAvailableProducts] = useState([]);
    const [productSearchTerm, setProductSearchTerm] = useState("");
    const debouncedProductSearch = useDebounce(productSearchTerm, 500);
    const [isLoadingProducts, setIsLoadingProducts] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Pagination / virtual scroll for available products
    const [productsSkip, setProductsSkip] = useState(0);
    const [productsLimit] = useState(10); // load 10 items per scroll
    const [hasMoreProducts, setHasMoreProducts] = useState(true);
    const [isFetchingMore, setIsFetchingMore] = useState(false);

    // State for delete confirmation
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    // Helper to get product image preview (supports both image objects and products with images/productOptions)
    const getProductPreview = (item) => {
        if (!item) return null;
        // If item is an image object with preview
        if (item.preview) return item.preview;
        // If product.images is an object with preview
        if (item.images && item.images.preview) return item.images.preview;
        // If product.images is an array
        if (Array.isArray(item.images) && item.images.length && item.images[0].preview) return item.images[0].preview;
        // If product has image field
        if (item.image && item.image.preview) return item.image.preview;
        // Variant-specific images inside productOptions
        if (item.productOptions && item.productOptions.length) {
            for (const opt of item.productOptions) {
                if (opt.images && opt.images.length && opt.images[0].preview) return opt.images[0].preview;
            }
        }
        return null;
    };

    // State for expanded mapping cards
    const [expandedMappings, setExpandedMappings] = useState({});

    // Fetch mappings
    const fetchMappings = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await getProductVariantMappings({ searchTerm: debouncedSearch });
            if (response.success) {
                setMappings(response.data || []);
                setTotalCount(response.totalCount || 0);
            }
        } catch (error) {
            console.error("Error fetching mappings:", error);
        } finally {
            setIsLoading(false);
        }
    }, [debouncedSearch]);

    useEffect(() => {
        fetchMappings();
    }, [fetchMappings]);

    // Fetch available products for mapping
    const fetchAvailableProducts = useCallback(async (skip = 0, overrideExcludeIds = null) => {
        if (skip === 0) {
            setIsLoadingProducts(true);
            setHasMoreProducts(true);
        } else {
            setIsFetchingMore(true);
        }

        try {
            // Get IDs to exclude (already selected as variants in this mapping) - allow override for immediate updates
            const excludeIds = overrideExcludeIds || selectedVariants.map(v => v._id);
            if (selectedPrimary) {
                excludeIds.push(selectedPrimary._id);
            }

            const response = await getProductsForVariantMapping({ 
                searchTerm: debouncedProductSearch,
                excludeIds,
                currentMappingId: editingMapping?._id,
                skip,
                limit: productsLimit
            });
            if (response.success) {
                const fetched = response.data || [];
                if (skip === 0) {
                    setAvailableProducts(fetched);
                } else {
                    setAvailableProducts(prev => [...prev, ...fetched]);
                }
                setHasMoreProducts(fetched.length === productsLimit);
                setProductsSkip(skip + fetched.length);
            }
        } catch (error) {
            console.error("Error fetching products:", error);
        } finally {
            setIsLoadingProducts(false);
            setIsFetchingMore(false);
        }
    }, [debouncedProductSearch, selectedPrimary, selectedVariants, editingMapping, productsLimit]);

    useEffect(() => {
        if (isModalOpen) {
            // reset pagination and fetch first page
            setProductsSkip(0);
            setHasMoreProducts(true);
            fetchAvailableProducts(0);
        }
    }, [isModalOpen, fetchAvailableProducts]);

    // refetch on search changes (debounced)
    useEffect(() => {
        if (isModalOpen) {
            setProductsSkip(0);
            setHasMoreProducts(true);
            fetchAvailableProducts(0);
        }
    }, [debouncedProductSearch, fetchAvailableProducts, isModalOpen]);

    // Handle opening create modal
    const handleOpenCreateModal = () => {
        setEditingMapping(null);
        setSelectedPrimary(null);
        setSelectedVariants([]);
        setProductSearchTerm("");
        setIsModalOpen(true);
    };

    // Handle opening edit modal
    const handleOpenEditModal = (mapping) => {
        setEditingMapping(mapping);
        setSelectedPrimary({
            _id: mapping.primaryProductId,
            productName: mapping.primaryProductName,
            images: mapping.primaryProductImage,
            productOptions: mapping.primaryProductOptions || []
        });
        setSelectedVariants(mapping.variantProducts || []);
        setProductSearchTerm("");
        setIsModalOpen(true);
    };

    // Handle selecting primary product
    const handleSelectPrimary = (product) => {
        setSelectedPrimary(product);
        // Remove from variants if it was there
        setSelectedVariants(prev => prev.filter(v => v._id !== product._id));
    };

    // Handle adding variant
    const handleAddVariant = (product) => {
        if (selectedPrimary && product._id === selectedPrimary._id) {
            return; // Can't add primary as variant
        }
        if (!selectedVariants.find(v => v._id === product._id)) {
            setSelectedVariants(prev => [...prev, product]);
        }
    };

    // Handle removing variant
    const handleRemoveVariant = (productId) => {
        const newVars = selectedVariants.filter(v => v._id !== productId);
        setSelectedVariants(newVars);
        // Refresh available products to include the removed variant
        // pass override exclude ids so fetch uses the updated list immediately
        fetchAvailableProducts(0, newVars.map(v => v._id));
    };

    // Handle save mapping
    const handleSaveMapping = async () => {
        if (!selectedPrimary) {
            toast.error("Please select a primary product");
            return;
        }
        if (selectedVariants.length === 0) {
            toast.error("Please add at least one variant product");
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                primaryProductId: selectedPrimary._id,
                variantProductIds: selectedVariants.map(v => v._id)
            };

            let response;
            if (editingMapping) {
                response = await updateProductVariantMapping({
                    _id: editingMapping._id,
                    ...payload
                });
            } else {
                response = await createProductVariantMapping(payload);
            }

            if (response.success) {
                setIsModalOpen(false);
                fetchMappings();
            } else {
                toast.error(response.message || "Failed to save mapping");
            }
        } catch (error) {
            console.error("Error saving mapping:", error);
            toast.error(error.message || "Failed to save mapping");
        } finally {
            setIsSaving(false);
        }
    };

    // Handle delete mapping
    const handleDeleteMapping = async (mappingId) => {
        try {
            const response = await deleteProductVariantMapping({ _id: mappingId });
            if (response.success) {
                setDeleteConfirm(null);
                fetchMappings();
            } else {
                toast.error(response.message || "Failed to delete mapping");
            }
        } catch (error) {
            console.error("Error deleting mapping:", error);
            toast.error("Failed to delete mapping");
        }
    };

    // Toggle expanded state
    const toggleExpanded = (mappingId) => {
        setExpandedMappings(prev => ({
            ...prev,
            [mappingId]: !prev[mappingId]
        }));
    };

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                        <BiLinkAlt className="text-white text-2xl" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Map Product Variants</h1>
                        <p className="text-gray-500 text-sm">Link products as variants to display them together in the customer app</p>
                    </div>
                </div>
            </div>

            {/* Search and Add Button */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="relative flex-1">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by primary product name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    />
                </div>
                <button
                    onClick={handleOpenCreateModal}
                    className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl"
                >
                    <FaPlus />
                    Create Mapping
                </button>
            </div>

            {/* Stats Card */}
            <div className="bg-white rounded-xl p-4 mb-6 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <FaBoxes className="text-purple-600" />
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">Total Variant Mappings</p>
                        <p className="text-2xl font-bold text-gray-900">{totalCount}</p>
                    </div>
                </div>
            </div>

            {/* Mappings List */}
            {isLoading ? (
                <div className="flex justify-center items-center py-20">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
                </div>
            ) : mappings.length === 0 ? (
                <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-100">
                    <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <BiLinkAlt className="text-purple-600 text-3xl" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No Variant Mappings Yet</h3>
                    <p className="text-gray-500 mb-4">Create variant mapping to link products together</p>
                    <button
                        onClick={handleOpenCreateModal}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-indigo-700 transition-all"
                    >
                        <FaPlus />
                        Create Mapping
                    </button>
                </div>
            ) : (
                <div className="space-y-4">
                    {mappings.map((mapping) => (
                        <div key={mapping._id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                            {/* Mapping Header */}
                            <div 
                                className="p-4 flex items-center gap-4 cursor-pointer hover:bg-gray-50 transition-colors"
                                onClick={() => toggleExpanded(mapping._id)}
                            >
                                {/* Primary Product Image */}
                                <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                                    {getProductPreview(mapping.primaryProductImage || { productOptions: mapping.primaryProductOptions }) ? (
                                        <img 
                                            src={getProductPreview(mapping.primaryProductImage || { productOptions: mapping.primaryProductOptions })} 
                                            alt={mapping.primaryProductName}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                                            <FaBoxes size={24} />
                                        </div>
                                    )}
                                </div>

                                {/* Primary Product Info */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-semibold rounded">
                                            PRIMARY
                                        </span>
                                    </div>
                                    <h3 className="font-semibold text-gray-900 truncate">
                                        {mapping.primaryProductName || "Unknown Product"}
                                    </h3>
                                    <p className="text-sm text-gray-500">
                                        {mapping.variantProducts?.length || 0} variant{mapping.variantProducts?.length !== 1 ? 's' : ''} linked
                                    </p>
                                </div>

                                {/* Variant Preview */}
                                <div className="hidden sm:flex items-center gap-1">
                                    {mapping.variantProducts?.slice(0, 4).map((variant, idx) => (
                                        <div 
                                            key={variant._id} 
                                            className="w-10 h-10 rounded-full overflow-hidden bg-gray-100 border-2 border-white shadow-sm"
                                            style={{ marginLeft: idx > 0 ? '-8px' : '0' }}
                                        >
                                            {getProductPreview(variant) ? (
                                                <img 
                                                    src={getProductPreview(variant)} 
                                                    alt={variant.productName}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                                                    <FaBoxes />
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    {mapping.variantProducts?.length > 4 && (
                                        <div className="w-10 h-10 rounded-lg bg-gray-100 border-2 border-white shadow-sm flex items-center justify-center text-xs font-semibold text-gray-600" style={{ marginLeft: '-8px' }}>
                                            +{mapping.variantProducts.length - 4}
                                        </div>
                                    )}
                                </div>

                                {/* Actions */}
                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleOpenEditModal(mapping); }}
                                        className="p-2 text-gray-600 hover:text-purple-800 hover:bg-purple-200 rounded-lg transition-colors"
                                        title="Edit Mapping"
                                    >
                                        <FiEdit2 size={16} />
                                    </button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setDeleteConfirm(mapping._id); }}
                                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-200 rounded-lg transition-colors"
                                        title="Delete Mapping"
                                    >
                                        <FaTrash size={16} />
                                    </button>
                                    <div className="text-gray-400">
                                        {expandedMappings[mapping._id] ? <FaChevronUp /> : <FaChevronDown />}
                                    </div>
                                </div>
                            </div>

                            {/* Expanded Variants */}
                            {expandedMappings[mapping._id] && (
                                <div className="border-t border-gray-100 p-4 bg-gray-50">
                                    <p className="text-sm font-semibold text-gray-700 mb-3">Linked Variants:</p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                        {mapping.variantProducts?.map((variant) => (
                                            <div 
                                                key={variant._id} 
                                                className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200"
                                            >
                                                <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                                                    {getProductPreview(variant) ? (
                                                        <img 
                                                            src={getProductPreview(variant)} 
                                                            alt={variant.productName}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                                                            <FaBoxes />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-medium text-gray-900 text-sm truncate">{variant.productName}</p>
                                                    <p className="text-xs text-gray-500">{variant.productId}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Create/Edit Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                        {/* Modal Header */}
                        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">
                                    {editingMapping ? "Edit Variant Mapping" : "Create Variant Mapping"}
                                </h2>
                                <p className="text-sm text-gray-500 mt-1">
                                    Select a primary product and add variants that will be displayed together
                                </p>
                            </div>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <FaTimes className="text-gray-400" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="flex-1 overflow-auto p-6">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* Left Column - Available Products */}
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-3">Available Products</h3>
                                    
                                    {/* Search */}
                                    <div className="relative mb-4">
                                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text"
                                            placeholder="Search products..."
                                            value={productSearchTerm}
                                            onChange={(e) => setProductSearchTerm(e.target.value)}
                                            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                        />
                                    </div>

                                    {/* Products List */}
                                    <div
                                        className="h-80 overflow-auto border border-gray-200 rounded-lg"
                                        onScroll={(e) => {
                                            const el = e.currentTarget;
                                            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50 && !isFetchingMore && hasMoreProducts) {
                                                // load next page
                                                fetchAvailableProducts(productsSkip);
                                            }
                                        }}
                                    >
                                        {isLoadingProducts && productsSkip === 0 ? (
                                            <div className="flex items-center justify-center h-full">
                                                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
                                            </div>
                                        ) : availableProducts.length === 0 ? (
                                            <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                                                No products available
                                            </div>
                                        ) : (
                                            <div className="divide-y divide-gray-100">
                                                {availableProducts.map((product) => {
                                                    const isPrimary = selectedPrimary?._id === product._id;
                                                    const isVariant = selectedVariants.some(v => v._id === product._id);
                                                    
                                                    return (
                                                        <div 
                                                            key={product._id}
                                                            className={`p-3 flex items-center gap-3 hover:bg-gray-50 ${isPrimary || isVariant ? 'bg-purple-50' : ''}`}
                                                        >
                                                            <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                                                                {getProductPreview(product) ? (
                                                                    <img 
                                                                        src={getProductPreview(product)} 
                                                                        alt={product.productName}
                                                                        className="w-full h-full object-cover"
                                                                    />
                                                                ) : (
                                                                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                                                                        <FaBoxes />
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="font-medium text-gray-900 text-sm truncate">{product.productName}</p>
                                                                <p className="text-xs text-gray-500">{product.productId}</p>
                                                            </div>
                                                            <div className="flex gap-2">
                                                                <button
                                                                    onClick={() => handleSelectPrimary(product)}
                                                                    disabled={isPrimary}
                                                                    className={`px-2 py-1 text-xs font-semibold rounded ${
                                                                        isPrimary 
                                                                            ? 'bg-purple-600 text-white' 
                                                                            : 'bg-gray-100 text-gray-600 hover:bg-purple-100 hover:text-purple-600'
                                                                    } transition-colors`}
                                                                >
                                                                    {isPrimary ? 'Primary' : 'Set Primary'}
                                                                </button>
                                                                {!isPrimary && (
                                                                    <button
                                                                        onClick={() => isVariant ? handleRemoveVariant(product._id) : handleAddVariant(product)}
                                                                        className={`px-2 py-1 text-xs font-semibold rounded ${
                                                                            isVariant 
                                                                                ? 'bg-green-600 text-white' 
                                                                                : 'bg-gray-100 text-gray-600 hover:bg-green-100 hover:text-green-600'
                                                                        } transition-colors`}
                                                                    >
                                                                        {isVariant ? <FaCheck /> : 'Add'}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                {isFetchingMore && (
                                                    <div className="flex items-center justify-center py-3">
                                                        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-purple-500"></div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Right Column - Selected Products */}
                                <div>
                                    {/* Primary Product */}
                                    <div className="mb-6">
                                        <h3 className="font-semibold text-gray-900 mb-3">Primary Product</h3>
                                        {selectedPrimary ? (
                                            <div className="p-4 bg-purple-50 border-2 border-purple-200 rounded-xl flex items-center gap-3">
                                                <div className="w-16 h-16 rounded-lg overflow-hidden bg-white flex-shrink-0">
                                                    {getProductPreview(selectedPrimary) ? (
                                                        <img 
                                                            src={getProductPreview(selectedPrimary)} 
                                                            alt={selectedPrimary.productName}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                                                            <FaBoxes size={24} />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex-1">
                                                    <span className="px-2 py-0.5 bg-purple-600 text-white text-xs font-semibold rounded mb-1 inline-block">
                                                        PRIMARY
                                                    </span>
                                                    <p className="font-semibold text-gray-900">{selectedPrimary.productName}</p>
                                                </div>
                                                <button
                                                    onClick={() => setSelectedPrimary(null)}
                                                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                >
                                                    <FaTimes />
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="p-8 border-2 border-dashed border-gray-200 rounded-xl text-center text-gray-500">
                                                Select a primary product from the list
                                            </div>
                                        )}
                                    </div>

                                    {/* Variants */}
                                    <div>
                                        <h3 className="font-semibold text-gray-900 mb-3">
                                            Linked Variants ({selectedVariants.length})
                                        </h3>
                                        {selectedVariants.length === 0 ? (
                                            <div className="p-8 border-2 border-dashed border-gray-200 rounded-xl text-center text-gray-500">
                                                Add variant products from the list
                                            </div>
                                        ) : (
                                            <div className="space-y-2 max-h-64 overflow-auto">
                                                {selectedVariants.map((variant) => (
                                                    <div 
                                                        key={variant._id}
                                                        className="p-3 bg-gray-50 border border-gray-200 rounded-lg flex items-center gap-3"
                                                    >
                                                        <div className="w-10 h-10 rounded-lg overflow-hidden bg-white flex-shrink-0">
                                                            {getProductPreview(variant) ? (
                                                                <img 
                                                                    src={getProductPreview(variant)} 
                                                                    alt={variant.productName}
                                                                    className="w-full h-full object-cover"
                                                                />
                                                            ) : (
                                                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                                                    <FaBoxes />
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="font-medium text-gray-900 text-sm truncate">{variant.productName}</p>
                                                        </div>
                                                        <button
                                                            onClick={() => handleRemoveVariant(variant._id)}
                                                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                                        >
                                                            <FaTimes size={12} />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="px-6 py-2 border border-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveMapping}
                                disabled={isSaving || !selectedPrimary || selectedVariants.length === 0}
                                className="px-6 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-semibold rounded-lg hover:from-purple-600 hover:to-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {isSaving ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <FaCheck />
                                        {editingMapping ? 'Update Mapping' : 'Create Mapping'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl p-6 max-w-md w-full">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <FaTrash className="text-red-600 text-2xl" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Mapping?</h3>
                            <p className="text-gray-500 mb-6">
                                This will unlink all variant products. They will appear as individual products again.
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setDeleteConfirm(null)}
                                    className="flex-1 px-4 py-2 border border-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => handleDeleteMapping(deleteConfirm)}
                                    className="flex-1 px-4 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MapProductVariants;
